import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { useUser } from "@/context/useUser";
import { Nfc, Search, CheckCircle2, Loader2, AlertCircle, Eye, Printer, UserCircle2, Calendar, ImageIcon, X, XCircle, RefreshCw, CreditCard, Mail, Building, Phone, Package as PackageIcon, DownloadCloud, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import toast from "react-hot-toast";

interface Period {
    period_id: number;
    name: string;
    is_active: number | boolean | string;
}

interface Registration {
    registration_id: number;
    participant: {
        participant_id: number;
        full_name: string;
        email: string;
        phone?: string;
        whatsapp_number?: string;
        institution?: string;
        address?: string;
    };
    rfid_number: string | null;
    is_printed?: number | boolean;
    print_batch?: number | null;
    payment_status: string;
    is_ready_to_pay: boolean;
    registration_number?: string;
    registration_date?: string;
    registration_type?: string;
    package?: { name: string };
    package_name?: string;
}

interface PIC {
    participant_id: number;
    full_name: string;
    institution: string | null;
    email: string | null;
    total_data: number;
}

export default function RfidRegisterPage() {
    const queryClient = useQueryClient();
    const { data: currentUser } = useUser();
    // Petugas RFID (role 7) hanya boleh mendaftarkan RFID — tidak boleh export/download data.
    const isPetugasRfid = currentUser?.role_id === 7;
    const [selectedPeriodId, setSelectedPeriodId] = useState<string>("");
    const [searchQuery, setSearchQuery] = useState("");
    const [filterPicId, setFilterPicId] = useState("all");
    const [filterInstitution, setFilterInstitution] = useState("");
    const [filterRfidStatus, setFilterRfidStatus] = useState("all");
    const [filterPrintStatus, setFilterPrintStatus] = useState("all");
    const [filterPrintBatch, setFilterPrintBatch] = useState("all");
    const [filterPaymentStatus, setFilterPaymentStatus] = useState("all");
    const [appliedFilters, setAppliedFilters] = useState({ picId: "all", institution: "", rfidStatus: "all", printStatus: "all", printBatch: "all", paymentStatus: "all" });
    const [isExporting, setIsExporting] = useState(false);
    const [isExportingZip, setIsExportingZip] = useState(false);
    const [isExportingZipPdf, setIsExportingZipPdf] = useState(false);
    const [isExportingQrZip, setIsExportingQrZip] = useState(false);
    const [exportLimit, setExportLimit] = useState<string>("");
    const [hdMode, setHdMode] = useState(false);

    // Urutan diambil berdasarkan registration_id (paling lama -> baru) supaya Excel, ZIP kartu,
    // dan ZIP QR selalu ambil N data pertama yang PERSIS SAMA saat "Jumlah Data" diisi.
    const buildExportParams = () => {
        const params = new URLSearchParams();
        params.append('period_id', selectedPeriodId);
        if (debouncedSearch) params.append('search', debouncedSearch);
        if (appliedFilters.picId && appliedFilters.picId !== "all") params.append('pic_id', appliedFilters.picId);
        if (appliedFilters.institution) params.append('institution', appliedFilters.institution);
        if (appliedFilters.rfidStatus && appliedFilters.rfidStatus !== "all") params.append('rfid_status', appliedFilters.rfidStatus);
        if (appliedFilters.printStatus && appliedFilters.printStatus !== "all") params.append('print_status', appliedFilters.printStatus);
        if (appliedFilters.printBatch && appliedFilters.printBatch !== "all") params.append('print_batch', appliedFilters.printBatch);
        if (appliedFilters.paymentStatus && appliedFilters.paymentStatus !== "all") params.append('payment_status', appliedFilters.paymentStatus);
        if (exportLimit && Number(exportLimit) > 0) params.append('limit', exportLimit);
        return params;
    };
    const [isBulkMarking, setIsBulkMarking] = useState(false);
    const [isBulkMarkConfirmOpen, setIsBulkMarkConfirmOpen] = useState(false);
    const [bulkMarkConfirmMessage, setBulkMarkConfirmMessage] = useState("");
    const [rfidModalData, setRfidModalData] = useState<Registration | null>(null);
    const [rfidCode, setRfidCode] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    // "oldest" = urutan registration_id ASC, SAMA PERSIS dengan urutan yang dipakai Export & Set
    // Tercetak, supaya baris ke-N di tabel = baris ke-N di file export saat memakai "Jumlah Data".
    const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
    const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    // Modal states
    const [selectedParticipant, setSelectedParticipant] = useState<Registration | null>(null);
    const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);
    const [cardBackImageUrl, setCardBackImageUrl] = useState<string | null>(null);
    const [isLoadingCard, setIsLoadingCard] = useState<boolean>(false);
    const [isDownloadingCard, setIsDownloadingCard] = useState<number | null>(null);
    const [isDownloadingCardBack, setIsDownloadingCardBack] = useState<number | null>(null);
    const [isCheckingPaymentStatus, setIsCheckingPaymentStatus] = useState<boolean>(false);
    const [rfidCodeForModal, setRfidCodeForModal] = useState("");
    const [rfidToDelete, setRfidToDelete] = useState<{ registrationId: number, rfidNumber: string } | null>(null);

    // Fetch Card Preview when a participant is selected
    React.useEffect(() => {
        let isMounted = true;
        if (!selectedParticipant?.participant?.participant_id || !selectedPeriodId) {
            setCardImageUrl(null);
            return;
        }

        const fetchCardPreview = async () => {
            setIsLoadingCard(true);
            try {
                const [imgRes, backImgRes] = await Promise.all([
                    axiosClient.get(
                        `/participants/${selectedParticipant.participant.participant_id}/registrations/period/${selectedPeriodId}/card-image`,
                        { responseType: "blob" }
                    ).catch(() => null),
                    axiosClient.get(
                        `/participants/${selectedParticipant.participant.participant_id}/registrations/period/${selectedPeriodId}/card-back-image`,
                        { responseType: "blob" }
                    ).catch(() => null)
                ]);

                if (!isMounted) return;

                if (imgRes && imgRes.data) {
                    const url = window.URL.createObjectURL(new Blob([imgRes.data]));
                    setCardImageUrl(url);
                }

                if (backImgRes && backImgRes.data) {
                    const backUrl = window.URL.createObjectURL(new Blob([backImgRes.data]));
                    setCardBackImageUrl(backUrl);
                }

                if (!imgRes && !backImgRes) {
                    toast.error("Gagal memuat preview kartu.");
                }
            } catch (error) {
                console.error("Gagal memuat kartu Konaspi:", error);
            } finally {
                if (isMounted) setIsLoadingCard(false);
            }
        };

        fetchCardPreview();

        return () => {
            isMounted = false;
        };
    }, [selectedParticipant?.participant?.participant_id, selectedPeriodId]);

    // Cleanup object URL
    React.useEffect(() => {
        return () => {
            if (cardImageUrl) window.URL.revokeObjectURL(cardImageUrl);
            if (cardBackImageUrl) window.URL.revokeObjectURL(cardBackImageUrl);
        };
    }, [cardImageUrl, cardBackImageUrl]);

    const printImage = (url: string, title: string) => {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(`
                <html>
                    <head>
                        <title>${title}</title>
                        <style>
                            body, html { margin: 0; padding: 0; width: 100%; height: 100%; background-color: #fff; }
                            img { width: 100vw; height: 100vh; object-fit: contain; display: block; margin: 0 auto; }
                            @media print {
                                @page { margin: 0; }
                                body, html { margin: 0; padding: 0; width: 100%; height: 100%; }
                                img { width: 100%; height: 100%; object-fit: fill; }
                            }
                        </style>
                    </head>
                    <body>
                        <img src="${url}" onload="window.print(); window.onafterprint = function() { window.close(); }" />
                    </body>
                </html>
            `);
            printWindow.document.close();
        } else {
            toast.error("Pop-up diblokir oleh browser. Izinkan pop-up untuk mencetak.");
        }
    };

    const handleDownloadCardDirect = async (participantId: number, regId: number, regNum: string, name: string) => {
        setIsDownloadingCard(participantId);
        try {
            const res = await axiosClient.get(
                `/participants/${participantId}/registrations/period/${selectedPeriodId}/card-image`,
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const cleanName = name.replace(/\s+/g, "_");
            const title = `ID_Card_KONASPI_${regNum || regId}_${cleanName}_Depan`;
            printImage(url, title);
            
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 10000); // Revoke after some time to ensure printing starts
            
            toast.success("Membuka dialog cetak kartu depan...");
        } catch (error) {
            console.error("Print error:", error);
            toast.error("Gagal menyiapkan kartu Konaspi untuk dicetak.");
        } finally {
            setIsDownloadingCard(null);
        }
    };

    const handleDownloadCardBackDirect = async (participantId: number, regId: number, regNum: string, name: string) => {
        setIsDownloadingCardBack(participantId);
        try {
            const res = await axiosClient.get(
                `/participants/${participantId}/registrations/period/${selectedPeriodId}/card-back-image`,
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const cleanName = name.replace(/\s+/g, "_");
            const title = `ID_Card_KONASPI_${regNum || regId}_${cleanName}_Belakang`;
            printImage(url, title);
            
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 10000); // Revoke after some time to ensure printing starts
            
            toast.success("Membuka dialog cetak kartu belakang...");
        } catch (error) {
            console.error("Print error:", error);
            toast.error("Gagal menyiapkan kartu Konaspi Belakang untuk dicetak.");
        } finally {
            setIsDownloadingCardBack(null);
        }
    };

    const handleCheckParticipantPaymentStatus = async () => {
        if (!selectedParticipant) return;
        setIsCheckingPaymentStatus(true);
        try {
            const resList = await axiosClient.get(`/payments/registrations/${selectedParticipant.registration_id}`);
            const payments = resList.data?.data;
            let paymentId = null;
            if (Array.isArray(payments) && payments.length > 0) {
                paymentId = payments[0].payment_id;
            }

            if (!paymentId) {
                toast.error("Belum ada data tagihan / Virtual Account yang dibuat untuk peserta ini.");
                setIsCheckingPaymentStatus(false);
                return;
            }

            const { data } = await axiosClient.get(`/payments/sync-status/${paymentId}`);

            const newStatus = data.status?.toLowerCase();
            if (newStatus === "paid") {
                toast.success(data.message || "Status berhasil disinkronkan: LUNAS (PAID)!");
            } else if (newStatus === "unpaid" || newStatus === "pending") {
                toast(data.message || `Status dari Bank: ${data.status?.toUpperCase() || "BELUM LUNAS"}`, { icon: 'ℹ️' });
            } else {
                toast.success(data.message || "Status pembayaran berhasil diperiksa.");
            }

            if (newStatus) {
                setSelectedParticipant((prev) => {
                    if (!prev) return prev;
                    return { ...prev, payment_status: newStatus };
                });
                refetch();
            }
        } catch (error: any) {
            console.error("Cek status error:", error);
            toast.error(error?.response?.data?.message || "Gagal sinkronisasi status pembayaran.");
        } finally {
            setIsCheckingPaymentStatus(false);
        }
    };

    const handlePrintCardFromModal = () => {
        if (!cardImageUrl || !selectedParticipant) return;
        const regNum = selectedParticipant.registration_number || `Reg_${selectedParticipant.registration_id}`;
        const cleanName = (selectedParticipant.participant.full_name || "Peserta").replace(/\s+/g, "_");
        const title = `ID_Card_KONASPI_${regNum}_${cleanName}_Depan`;
        printImage(cardImageUrl, title);
        toast.success("Membuka dialog cetak kartu Konaspi (Depan)...");
    };

    const handlePrintCardBackFromModal = () => {
        if (!cardBackImageUrl || !selectedParticipant) return;
        const regNum = selectedParticipant.registration_number || `Reg_${selectedParticipant.registration_id}`;
        const cleanName = (selectedParticipant.participant.full_name || "Peserta").replace(/\s+/g, "_");
        const title = `ID_Card_KONASPI_${regNum}_${cleanName}_Belakang`;
        printImage(cardBackImageUrl, title);
        toast.success("Membuka dialog cetak kartu Konaspi (Belakang)...");
    };


    // Fetch Periods
    const { data: periods } = useQuery({
        queryKey: ["periods"],
        queryFn: async (): Promise<Period[]> => {
            const response = await axiosClient.get(`/periods`);
            return response.data;
        },
        refetchOnWindowFocus: false
    });

    // Auto-select active period
    const { data: activePeriod } = useActivePeriod();

    React.useEffect(() => {
        if (!selectedPeriodId) {
            if (activePeriod) {
                setSelectedPeriodId(activePeriod.id.toString());
            } else if (periods && periods.length > 0) {
                setSelectedPeriodId(periods[0].period_id.toString());
            }
        }
    }, [activePeriod, periods, selectedPeriodId]);

    // Fetch PICs for filter dropdown
    const { data: picList } = useQuery({
        queryKey: ["pics"],
        queryFn: async (): Promise<PIC[]> => {
            const response = await axiosClient.get(`/participants/pics`);
            return response.data;
        },
        refetchOnWindowFocus: false
    });

    // Fetch Registrations
    const { data: queryData, isLoading: loadingRegistrations, isFetching, refetch } = useQuery({
        queryKey: ["registrations", selectedPeriodId, appliedFilters, currentPage, perPage, debouncedSearch, sortOrder],
        queryFn: async (): Promise<any> => {
            if (!selectedPeriodId) return null;

            const params = new URLSearchParams();
            params.append('period_id', selectedPeriodId);
            params.append('page', currentPage.toString());
            params.append('per_page', perPage.toString());
            params.append('sort', sortOrder);
            if (debouncedSearch) params.append('search', debouncedSearch);
            if (appliedFilters.picId && appliedFilters.picId !== "all") params.append('pic_id', appliedFilters.picId);
            if (appliedFilters.institution) params.append('institution', appliedFilters.institution);
            if (appliedFilters.rfidStatus && appliedFilters.rfidStatus !== "all") params.append('rfid_status', appliedFilters.rfidStatus);
            if (appliedFilters.printStatus && appliedFilters.printStatus !== "all") params.append('print_status', appliedFilters.printStatus);
            if (appliedFilters.printBatch && appliedFilters.printBatch !== "all") params.append('print_batch', appliedFilters.printBatch);
            if (appliedFilters.paymentStatus && appliedFilters.paymentStatus !== "all") params.append('payment_status', appliedFilters.paymentStatus);

            const response = await axiosClient.get(`/registrations`, { params });
            return response.data;
        },
        enabled: !!selectedPeriodId,
        refetchOnWindowFocus: false
    });

    const registrations: Registration[] = queryData?.data || [];
    const pagination = queryData || {};
    const statistics = queryData?.statistics || null;

    const assignMutation = useMutation({
        mutationFn: async ({ registrationId, rfidCardId }: { registrationId: number, rfidCardId: string }) => {
            const response = await axiosClient.post(`/rfid/assign/${registrationId}`, {
                rfid_number: rfidCardId
            });
            return response.data;
        },
        onSuccess: (data) => {
            toast.success(data.message || "Kartu RFID berhasil didaftarkan.");
            queryClient.invalidateQueries({ queryKey: ["registrations", selectedPeriodId] });

            // Perbarui state modal agar realtime berubah di layar jika sedang buka modal Detail
            if (selectedParticipant) {
                setSelectedParticipant((prev) => prev ? { ...prev, rfid_number: rfidCodeForModal || prev.rfid_number } : prev);
            }

            setRfidModalData(null);
            setRfidCode("");
            setRfidCodeForModal("");
            refetch();
        },
        onError: (error: unknown) => {
            let msg = "Gagal mendaftarkan kartu RFID.";
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as any;
                msg = axiosError.response?.data?.message || msg;
            } else if (error instanceof Error) {
                msg = error.message;
            }
            toast.error(msg);
        }
    });

    const deleteRfidMutation = useMutation({
        mutationFn: async (registrationId: number) => {
            const response = await axiosClient.delete(`/rfid/delete/${registrationId}`);
            return response.data;
        },
        onSuccess: (data) => {
            toast.success(data.message || "Kartu RFID berhasil dihapus.");
            queryClient.invalidateQueries({ queryKey: ["registrations", selectedPeriodId] });

            if (selectedParticipant) {
                setSelectedParticipant((prev) => prev ? { ...prev, rfid_number: null } : prev);
            }
            
            setRfidToDelete(null);
            refetch();
        },
        onError: (error: unknown) => {
            let msg = "Gagal menghapus kartu RFID.";
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as any;
                msg = axiosError.response?.data?.message || msg;
            } else if (error instanceof Error) {
                msg = error.message;
            }
            toast.error(msg);
        }
    });

    const handleDeleteRfid = (registrationId: number, rfidNumber: string) => {
        setRfidToDelete({ registrationId, rfidNumber });
    };

    const confirmDeleteRfid = () => {
        if (rfidToDelete) {
            deleteRfidMutation.mutate(rfidToDelete.registrationId);
        }
    };

    const togglePrintMutation = useMutation({
        mutationFn: async ({ registrationId, action }: { registrationId: number; action: 'mark' | 'unmark' }) => {
            const response = await axiosClient.put(`/registrations/${registrationId}/${action}-printed`);
            return response.data;
        },
        onSuccess: (data, variables) => {
            toast.success(data.message || "Status cetak berhasil diubah!");
            if (selectedParticipant && selectedParticipant.registration_id === variables.registrationId) {
                setSelectedParticipant((prev) => prev ? { ...prev, is_printed: variables.action === 'mark' ? 1 : 0 } : prev);
            }
            refetch();
        },
        onError: () => {
            toast.error("Gagal mengubah status cetak kartu.");
        }
    });

    const handleAssign = (e: React.FormEvent) => {
        e.preventDefault();
        if (!rfidModalData || !rfidCode) {
            toast.error("Pilih peserta dan tap kartu RFID.");
            return;
        }
        assignMutation.mutate({ registrationId: rfidModalData.registration_id, rfidCardId: rfidCode });
    };

    const handleExportExcel = async () => {
        if (!selectedPeriodId) {
            toast.error("Pilih acara (periode) terlebih dahulu.");
            return;
        }
        setIsExporting(true);
        try {
            const params = buildExportParams();

            const response = await axiosClient.get(`/registrations/export-rfid`, {
                params,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Data_RFID_Konaspi_${new Date().getTime()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Data berhasil diekspor!");
        } catch (error) {
            console.error("Export error:", error);
            toast.error("Gagal mengekspor data Excel.");
        } finally {
            setIsExporting(false);
        }
    };

    const applyFilters = () => {
        setAppliedFilters({
            picId: filterPicId,
            institution: filterInstitution,
            rfidStatus: filterRfidStatus,
            printStatus: filterPrintStatus,
            printBatch: filterPrintBatch,
            paymentStatus: filterPaymentStatus
        });
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterPicId("all");
        setFilterInstitution("");
        setFilterRfidStatus("all");
        setFilterPrintStatus("all");
        setFilterPrintBatch("all");
        setFilterPaymentStatus("all");
        setSearchQuery("");
        setDebouncedSearch("");
        setAppliedFilters({ picId: "all", institution: "", rfidStatus: "all", printStatus: "all", printBatch: "all", paymentStatus: "all" });
        setCurrentPage(1);
    };

    // Daftar gelombang cetak yang sudah pernah dibuat (untuk filter/cetak ulang)
    const { data: printBatches } = useQuery({
        queryKey: ["print-batches", selectedPeriodId],
        queryFn: async (): Promise<{ print_batch: number; total: number; printed_at: string | null }[]> => {
            if (!selectedPeriodId) return [];
            const response = await axiosClient.get(`/registrations/print-batches`, { params: { period_id: selectedPeriodId } });
            return response.data;
        },
        enabled: !!selectedPeriodId,
    });

    const handleExportZip = async () => {
        if (!selectedPeriodId) {
            toast.error("Pilih acara (periode) terlebih dahulu.");
            return;
        }
        setIsExportingZip(true);
        try {
            const params = buildExportParams();
            if (hdMode) params.append('hd', '1');

            const response = await axiosClient.get(`/registrations/export-zip`, {
                params,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Kartu_Konaspi_XII_${new Date().getTime()}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("File ZIP Kartu berhasil diunduh!");
        } catch (error) {
            console.error("Export ZIP error:", error);
            toast.error("Gagal mengekspor file ZIP Kartu.");
        } finally {
            setIsExportingZip(false);
        }
    };

    const handleExportZipPdf = async (mode: "separate" | "combined") => {
        if (!selectedPeriodId) {
            toast.error("Pilih acara (periode) terlebih dahulu.");
            return;
        }
        setIsExportingZipPdf(true);
        try {
            const params = buildExportParams();
            if (hdMode) params.append('hd', '1');
            params.append('mode', mode);

            const response = await axiosClient.get(`/registrations/export-zip-pdf`, {
                params,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            const extension = mode === "combined" ? "pdf" : "zip";
            link.setAttribute("download", `Kartu_PDF_Konaspi_XII_${new Date().getTime()}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(mode === "combined" ? "File PDF gabungan berhasil diunduh!" : "File ZIP PDF Kartu berhasil diunduh!");
        } catch (error) {
            console.error("Export ZIP PDF error:", error);
            toast.error("Gagal mengekspor file PDF Kartu.");
        } finally {
            setIsExportingZipPdf(false);
        }
    };

    const handleExportQrZip = async () => {
        if (!selectedPeriodId) {
            toast.error("Pilih acara (periode) terlebih dahulu.");
            return;
        }
        setIsExportingQrZip(true);
        try {
            const params = buildExportParams();

            const response = await axiosClient.get(`/registrations/export-qr-zip`, {
                params,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `QRCode_Konaspi_XII_${new Date().getTime()}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("File ZIP QR Code berhasil diunduh!");
        } catch (error) {
            console.error("Export QR ZIP error:", error);
            toast.error("Gagal mengekspor file ZIP QR Code.");
        } finally {
            setIsExportingQrZip(false);
        }
    };

    const handleBulkMarkPrinted = () => {
        if (!selectedPeriodId) return;

        if (appliedFilters.printStatus !== "unprinted") {
            toast.error('Pilih "Status Cetak: Belum Dicetak" terlebih dahulu sebelum menandai gelombang baru, agar kartu yang sudah dicetak sebelumnya tidak ikut tertimpa.');
            return;
        }

        const totalUnprinted = statistics?.unprinted ?? 0;
        const limitNum = exportLimit && Number(exportLimit) > 0 ? Number(exportLimit) : null;
        const confirmMessage = limitNum && limitNum < totalUnprinted
            ? `Tandai ${limitNum} peserta PERTAMA (dari ${totalUnprinted} peserta belum dicetak sesuai filter saat ini, urutan registrasi terlama) sebagai SUDAH DICETAK pada gelombang baru? Sisanya (${totalUnprinted - limitNum} peserta) bisa ditandai menyusul di gelombang berikutnya.`
            : "Anda yakin ingin menandai semua peserta yang terfilter (belum dicetak) sebagai SUDAH DICETAK pada gelombang baru?";
        setBulkMarkConfirmMessage(confirmMessage);
        setIsBulkMarkConfirmOpen(true);
    };

    const confirmBulkMarkPrinted = async () => {
        setIsBulkMarkConfirmOpen(false);
        setIsBulkMarking(true);
        try {
            // buildExportParams() sudah menyertakan "limit" (Jumlah Data) yang sama dipakai
            // Excel/ZIP, supaya bisa menandai sebagian dulu (mis. 500 dari 652 peserta).
            const params = buildExportParams();

            const response = await axiosClient.put(`/registrations/bulk-mark-printed?${params.toString()}`);
            toast.success(response.data.message || "Berhasil menandai status cetak.");
            setFilterPrintStatus("all");
            setAppliedFilters((prev) => ({ ...prev, printStatus: "all" }));
            refetch();
            // Supaya dropdown "Gelombang Cetak" langsung menampilkan gelombang baru tanpa refresh manual.
            queryClient.invalidateQueries({ queryKey: ["print-batches", selectedPeriodId] });
        } catch (error) {
            console.error("Bulk mark error:", error);
            toast.error("Gagal menandai status cetak massal.");
        } finally {
            setIsBulkMarking(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                            <Nfc className="w-6 h-6" />
                        </div>
                        <h1 className="text-xl md:text-2xl lg:text-2xl font-black text-slate-900 dark:text-white tracking-tight">Daftarkan RFID Peserta</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Pilih acara (periode) kemudian pilih peserta untuk mendaftarkan kartu fisik RFID.
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <Select value={selectedPeriodId} onValueChange={setSelectedPeriodId}>
                        <SelectTrigger className="h-11 rounded-xl w-[220px]"><SelectValue placeholder="Pilih Acara (Periode)" /></SelectTrigger>
                        <SelectContent>
                            {periods?.map(p => (
                                <SelectItem key={p.period_id} value={p.period_id.toString()}>
                                    {p.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Toolbar — dibungkus 1 card supaya rapi (pola sama seperti admin/event-guests) */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm mb-6 p-4 md:p-5 space-y-4">
                {/* Baris pencarian */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Cari nama / email peserta..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full h-10 pl-11 pr-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium"
                        />
                    </div>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Select value={sortOrder} onValueChange={(v) => { setSortOrder(v as "newest" | "oldest"); setCurrentPage(1); }}>
                                <SelectTrigger className="h-10 rounded-xl w-[170px] shrink-0"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="newest">Terbaru Dulu</SelectItem>
                                    <SelectItem value="oldest">Terlama Dulu</SelectItem>
                                </SelectContent>
                            </Select>
                        </TooltipTrigger>
                        <TooltipContent side="top"><p>"Terlama Dulu" memakai urutan yang sama dengan Export & Set Tercetak, jadi baris ke-N di sini = baris ke-N di file export</p></TooltipContent>
                    </Tooltip>
                    <Button
                        variant="outline"
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="h-10 rounded-xl shrink-0 font-semibold"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                {/* Baris filter — grid responsif supaya label tidak terpotong */}
                <div>
                    <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Filter</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                        <Select value={filterPicId} onValueChange={setFilterPicId}>
                            <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="PIC Kolektif" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua PIC</SelectItem>
                                {picList?.map(pic => (
                                    <SelectItem key={pic.participant_id} value={pic.participant_id.toString()}>
                                        {pic.full_name} {pic.institution ? `(${pic.institution})` : ''} {pic.email ? `- ${pic.email}` : ''} [{pic.total_data} data]
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <input
                            type="text"
                            placeholder="Institusi (Ganesha)"
                            value={filterInstitution}
                            onChange={(e) => setFilterInstitution(e.target.value)}
                            className="h-10 px-3 w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 outline-none font-medium"
                        />
                        <Select value={filterRfidStatus} onValueChange={setFilterRfidStatus}>
                            <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Status RFID" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua RFID</SelectItem>
                                <SelectItem value="registered">Sudah RFID</SelectItem>
                                <SelectItem value="unregistered">Belum RFID</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterPrintStatus} onValueChange={setFilterPrintStatus}>
                            <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Status Cetak" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Cetak</SelectItem>
                                <SelectItem value="printed">Sudah Dicetak</SelectItem>
                                <SelectItem value="unprinted">Belum Dicetak</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterPrintBatch} onValueChange={setFilterPrintBatch}>
                            <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Gelombang Cetak" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Gelombang</SelectItem>
                                {printBatches?.map((b) => (
                                    <SelectItem key={b.print_batch} value={b.print_batch.toString()}>
                                        Gelombang {b.print_batch} ({b.total} kartu)
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterPaymentStatus} onValueChange={setFilterPaymentStatus}>
                            <SelectTrigger className="h-10 rounded-xl w-full"><SelectValue placeholder="Pembayaran" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Bayar</SelectItem>
                                <SelectItem value="paid">Lunas (Paid)</SelectItem>
                                <SelectItem value="unpaid">Belum Lunas</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Action Toolbar: Filter Actions + Export & Bulk Action */}
                <div className="border-t border-slate-100 dark:border-slate-800 pt-4 flex flex-wrap items-center justify-end gap-2">
                    {(appliedFilters.picId !== "all" || appliedFilters.institution !== "" || appliedFilters.rfidStatus !== "all" || appliedFilters.printStatus !== "all" || appliedFilters.printBatch !== "all" || appliedFilters.paymentStatus !== "all") && (
                        <Button
                            onClick={resetFilters}
                            variant="ghost"
                            className="h-11 px-4 rounded-xl text-rose-500 hover:text-rose-600 hover:bg-rose-50 font-bold"
                        >
                            Reset
                        </Button>
                    )}
                    <Button
                        onClick={applyFilters}
                        className="h-11 px-6 rounded-xl bg-slate-800 hover:bg-slate-900 text-white dark:bg-slate-700 dark:hover:bg-slate-600 font-bold shadow-sm"
                    >
                        Terapkan Filter
                    </Button>
                    {!isPetugasRfid && (
                        <>
                            <div className="flex items-center gap-1.5" title="Batasi jumlah data yang diambil Excel, ZIP Kartu, ZIP QR, DAN ditandai oleh 'Set Tercetak' (kosongkan untuk semua data sesuai filter)">
                                <input
                                    type="number"
                                    min={1}
                                    placeholder="Jumlah Data"
                                    value={exportLimit}
                                    onChange={(e) => setExportLimit(e.target.value)}
                                    className="w-[130px] h-11 px-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none shadow-sm font-medium"
                                />
                            </div>
                            <Button
                                onClick={handleExportExcel}
                                disabled={isExporting || !selectedPeriodId}
                                className="h-11 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm font-bold"
                            >
                                {isExporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DownloadCloud className="w-4 h-4 mr-2" />}
                                Excel
                            </Button>
                            <label
                                className="flex items-center gap-1.5 h-11 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm text-sm font-semibold text-slate-600 dark:text-slate-300 cursor-pointer select-none"
                                title="Render kartu di resolusi 2x (QR & teks lebih tajam) - dipakai untuk kebutuhan cetak fisik"
                            >
                                <input
                                    type="checkbox"
                                    checked={hdMode}
                                    onChange={(e) => setHdMode(e.target.checked)}
                                    className="w-4 h-4 accent-amber-600"
                                />
                                HD
                            </label>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        disabled={isExportingZip || isExportingZipPdf || isExportingQrZip || !selectedPeriodId}
                                        className="h-11 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white shadow-sm font-bold"
                                    >
                                        {(isExportingZip || isExportingZipPdf || isExportingQrZip) ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DownloadCloud className="w-4 h-4 mr-2" />}
                                        ZIP
                                        <ChevronDown className="w-4 h-4 ml-2" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-[260px] rounded-xl z-[99] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl">
                                    <DropdownMenuLabel className="text-xs text-slate-500">Unduh ZIP</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleExportZip} disabled={isExportingZip}>
                                        Kartu Lengkap - JPG (Depan)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleExportZipPdf("separate")} disabled={isExportingZipPdf}>
                                        Kartu Lengkap - PDF (Banyak File)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleExportZipPdf("combined")} disabled={isExportingZipPdf}>
                                        Kartu Lengkap - PDF (1 File Gabungan)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleExportQrZip} disabled={isExportingQrZip}>
                                        QR Code Saja (untuk Bank)
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <div className="w-px self-stretch bg-slate-200 dark:bg-slate-700 mx-1 hidden sm:block" />
                        </>
                    )}

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={handleBulkMarkPrinted}
                                disabled={isBulkMarking || !selectedPeriodId}
                                className="h-11 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-bold"
                            >
                                {isBulkMarking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Nfc className="w-4 h-4 mr-2" />}
                                Set Tercetak
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top"><p>Tandai peserta yang cocok filter saat ini sebagai sudah dicetak sekaligus (isi "Jumlah Data" untuk menandai sebagian dulu, mis. 500 dari 652)</p></TooltipContent>
                    </Tooltip>
                </div>
            </div>

            {/* RFID Assignment Modal */}
            <Dialog open={!!rfidModalData} onOpenChange={(open) => !open && setRfidModalData(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Daftarkan Kartu RFID</DialogTitle>
                    </DialogHeader>
                    {rfidModalData && (
                        <div className="space-y-4">
                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                                <p className="font-bold text-slate-900 dark:text-white text-base">{rfidModalData.participant?.full_name}</p>
                                <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {rfidModalData.participant?.email}</p>
                                {rfidModalData.participant?.institution && (
                                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Building className="w-3.5 h-3.5" /> {rfidModalData.participant?.institution}</p>
                                )}
                            </div>
                            <form onSubmit={handleAssign} className="flex flex-col gap-4">
                                <div>
                                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 block">
                                        Tap / Scan Kartu RFID
                                    </label>
                                    <div className="relative">
                                        <Nfc className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-500" />
                                        <input
                                            type="text"
                                            autoFocus
                                            placeholder="Arahkan kursor kesini lalu tap kartu..."
                                            value={rfidCode}
                                            onChange={(e) => setRfidCode(e.target.value)}
                                            className="w-full h-12 pl-11 pr-4 bg-white dark:bg-slate-950 border-2 border-indigo-200 focus:border-indigo-500 rounded-xl text-lg font-mono placeholder:font-sans outline-none transition-all shadow-sm"
                                        />
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2">
                                        Pastikan kursor berada di dalam kotak di atas sebelum melakukan tap kartu.
                                    </p>
                                </div>
                                <div className="flex gap-2 justify-end pt-2 border-t border-slate-100 dark:border-slate-800 mt-2">
                                    <Button type="button" variant="ghost" onClick={() => setRfidModalData(null)}>Batal</Button>
                                    <Button type="submit" disabled={!rfidCode || assignMutation.isPending} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                        {assignMutation.isPending ? <Loader2 className="animate-spin mr-2 w-4 h-4" /> : <Nfc className="mr-2 w-4 h-4" />}
                                        Daftarkan RFID
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Statistics Banner */}
            {statistics && (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm mb-6">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-y-6 lg:gap-0 lg:divide-x divide-slate-100 dark:divide-slate-800">
                        <div className="flex flex-col items-center justify-center">
                            <span className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1.5">Total Peserta</span>
                            <span className="text-2xl font-black text-slate-900 dark:text-white">{statistics.total}</span>
                        </div>
                        <div className="flex flex-col items-center justify-center">
                            <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 mb-1.5"><Nfc className="w-3.5 h-3.5" /> RFID Terdaftar</span>
                            <span className="text-2xl font-black text-emerald-700 dark:text-emerald-300">{statistics.rfid_registered}</span>
                        </div>
                        <div className="flex flex-col items-center justify-center">
                            <span className="text-rose-500 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 mb-1.5"><Nfc className="w-3.5 h-3.5 opacity-50" /> RFID Belum</span>
                            <span className="text-2xl font-black text-rose-600">{statistics.rfid_unregistered}</span>
                        </div>
                        <div className="flex flex-col items-center justify-center">
                            <span className="text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 mb-1.5"><Printer className="w-3.5 h-3.5" /> Sudah Dicetak</span>
                            <span className="text-2xl font-black text-blue-700 dark:text-blue-300">{statistics.printed}</span>
                        </div>
                        <div className="flex flex-col items-center justify-center">
                            <span className="text-slate-500 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 mb-1.5"><Printer className="w-3.5 h-3.5 opacity-50" /> Belum Dicetak</span>
                            <span className="text-2xl font-black text-slate-700 dark:text-slate-300">{statistics.unprinted}</span>
                        </div>
                        <div className="flex flex-col items-center justify-center">
                            <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 mb-1.5">Lunas (Paid)</span>
                            <span className="text-2xl font-black text-emerald-700 dark:text-emerald-300">{statistics.paid ?? 0}</span>
                        </div>
                        <div className="flex flex-col items-center justify-center">
                            <span className="text-amber-500 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 mb-1.5">Belum Lunas</span>
                            <span className="text-2xl font-black text-amber-600">{statistics.unpaid ?? 0}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                {isFetching && !loadingRegistrations && (
                    <div className="absolute inset-0 z-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                <div className="overflow-x-auto pb-4">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-5 border-b border-slate-100 dark:border-slate-800 w-10 text-center"></th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 w-16">ID Reg</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Peserta</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Institusi & Paket</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Pembayaran</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Status RFID</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 bg-slate-50 dark:bg-slate-800/50 z-10 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.05)]">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {!selectedPeriodId ? (
                                <tr>
                                    <td colSpan={7} className="p-12 text-center text-slate-500 flex flex-col items-center">
                                        <AlertCircle className="w-10 h-10 mb-3 opacity-50" />
                                        Silakan pilih periode/acara terlebih dahulu.
                                    </td>
                                </tr>
                            ) : loadingRegistrations ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
                                        Memuat data peserta...
                                    </td>
                                </tr>
                            ) : registrations.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                        Tidak ada data yang ditemukan.
                                    </td>
                                </tr>
                            ) : (
                                registrations.map(reg => (
                                    <React.Fragment key={reg.registration_id}>
                                        <tr
                                            className={`group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors cursor-pointer ${rfidModalData?.registration_id === reg.registration_id ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'bg-white dark:bg-slate-900'}`}
                                            onClick={() => setExpandedRowId(expandedRowId === reg.registration_id ? null : reg.registration_id)}
                                        >
                                            <td className="px-4 py-4 text-slate-400 w-10 text-center">
                                                {expandedRowId === reg.registration_id ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                                            </td>
                                            <td className="px-6 py-4 font-black text-slate-600 dark:text-slate-400">#{reg.registration_id}</td>
                                            <td className="px-6 py-4">
                                                <p className="font-bold text-slate-900 dark:text-white text-sm">{reg.participant?.full_name}</p>
                                                <p className="text-slate-500 text-xs mt-0.5">{reg.participant?.email}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm capitalize">{reg.participant?.institution || "-"}</p>
                                                <p className="text-slate-500 text-xs mt-0.5 max-w-[200px] truncate" title={reg.package_name || reg.package?.name}>
                                                    {reg.package_name || reg.package?.name || "-"}
                                                </p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col items-start gap-1.5">
                                                    {reg.payment_status === "paid" ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
                                                            Lunas
                                                        </span>
                                                    ) : reg.payment_status === "pending" ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider">
                                                            Menunggu
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                                                            Belum Bayar
                                                        </span>
                                                    )}
                                                    <span className="text-[10px] text-slate-400 font-mono bg-slate-50 dark:bg-slate-800 px-2 py-0.5 rounded">
                                                        {reg.registration_number || "-"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-2">
                                                    {reg.rfid_number ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 text-[10px] font-bold uppercase tracking-wider w-fit">
                                                            <Nfc className="w-3.5 h-3.5" />
                                                            {reg.rfid_number}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wider w-fit">
                                                            <Nfc className="w-3.5 h-3.5 opacity-50" />
                                                            Belum Ada Kartu
                                                        </span>
                                                    )}

                                                    {togglePrintMutation.isPending && togglePrintMutation.variables?.registrationId === reg.registration_id ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wider w-fit">
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                            Memproses...
                                                        </span>
                                                    ) : reg.is_printed ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider w-fit">
                                                            <Printer className="w-3 h-3" />
                                                            Sudah Dicetak{reg.print_batch != null ? ` (Gel. ${reg.print_batch})` : ""}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-[10px] font-bold uppercase tracking-wider w-fit">
                                                            <Printer className="w-3 h-3" />
                                                            Belum Dicetak
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 sticky right-0 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 shadow-[inset_1px_0_0_rgba(0,0,0,0.05)] group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/20 transition-colors">
                                                <div className="flex items-center gap-2 justify-end">
                                                    {/* Detail Button */}
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                type="button"
                                                                size="icon"
                                                                variant="outline"
                                                                className="h-8 w-8 text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700 dark:border-blue-800 dark:hover:bg-blue-900/30 shadow-sm shrink-0"
                                                                onClick={(e) => { e.stopPropagation(); setSelectedParticipant(reg); }}
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top">
                                                            <p>Lihat Detail</p>
                                                        </TooltipContent>
                                                    </Tooltip>

                                                    {/* RFID Button */}
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                type="button"
                                                                size="icon"
                                                                variant="outline"
                                                                className="h-8 w-8 text-indigo-600 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 dark:border-indigo-800 dark:hover:bg-indigo-900/30 shadow-sm shrink-0"
                                                                onClick={(e) => { e.stopPropagation(); setRfidModalData(reg); setRfidCode(""); }}
                                                            >
                                                                <Nfc className="w-4 h-4" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top">
                                                            <p>Daftarkan RFID</p>
                                                        </TooltipContent>
                                                    </Tooltip>

                                                    {/* Print Dropdown */}
                                                    <DropdownMenu>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div>
                                                                    <DropdownMenuTrigger
                                                                        className="h-8 w-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center outline-none focus:ring-2 focus:ring-slate-500 transition-colors bg-white dark:bg-slate-900 shadow-sm"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <span className="sr-only">Menu Cetak</span>
                                                                        <Printer className="h-4 w-4" />
                                                                    </DropdownMenuTrigger>
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top">
                                                                <p>Cetak & Unduh</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                        <DropdownMenuContent align="end" className="w-[190px] rounded-xl z-[99] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl">
                                                            <DropdownMenuLabel className="text-xs text-slate-500">Menu Cetak</DropdownMenuLabel>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDownloadCardDirect(reg.participant.participant_id, reg.registration_id, reg.registration_number || '', reg.participant.full_name);
                                                                }}
                                                                disabled={isDownloadingCard === reg.participant.participant_id}
                                                                className="cursor-pointer"
                                                            >
                                                                {isDownloadingCard === reg.participant.participant_id ? (
                                                                    <Loader2 className="mr-2 h-4 w-4 text-emerald-500 animate-spin" />
                                                                ) : (
                                                                    <Printer className="mr-2 h-4 w-4 text-emerald-500" />
                                                                )}
                                                                <span>Print Depan Kartu</span>
                                                            </DropdownMenuItem>

                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDownloadCardBackDirect(reg.participant.participant_id, reg.registration_id, reg.registration_number || '', reg.participant.full_name);
                                                                }}
                                                                disabled={isDownloadingCardBack === reg.participant.participant_id}
                                                                className="cursor-pointer"
                                                            >
                                                                {isDownloadingCardBack === reg.participant.participant_id ? (
                                                                    <Loader2 className="mr-2 h-4 w-4 text-emerald-500 animate-spin" />
                                                                ) : (
                                                                    <Printer className="mr-2 h-4 w-4 text-emerald-500" />
                                                                )}
                                                                <span>Print Belakang Kartu</span>
                                                            </DropdownMenuItem>

                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    togglePrintMutation.mutate({ registrationId: reg.registration_id, action: reg.is_printed ? 'unmark' : 'mark' });
                                                                }}
                                                                className="cursor-pointer"
                                                                disabled={togglePrintMutation.isPending && togglePrintMutation.variables?.registrationId === reg.registration_id}
                                                            >
                                                                {togglePrintMutation.isPending && togglePrintMutation.variables?.registrationId === reg.registration_id ? (
                                                                    <Loader2 className="mr-2 h-4 w-4 text-slate-500 animate-spin" />
                                                                ) : reg.is_printed ? (
                                                                    <XCircle className="mr-2 h-4 w-4 text-rose-500" />
                                                                ) : (
                                                                    <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-500" />
                                                                )}
                                                                <span>{reg.is_printed ? 'Batal Tandai Cetak' : 'Tandai Sudah Print'}</span>
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedRowId === reg.registration_id && (
                                            <tr className="bg-slate-50/50 dark:bg-slate-800/30">
                                                <td colSpan={7} className="px-6 py-4 border-b border-slate-100 dark:border-slate-800">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm text-slate-600 dark:text-slate-300">
                                                        <div className="space-y-3">
                                                            <div className="flex items-center gap-2 font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                                                                <UserCircle2 className="w-4 h-4 text-blue-500" /> Data Personal
                                                            </div>
                                                            <div className="grid grid-cols-[100px_1fr] gap-2">
                                                                <span className="text-slate-400">Nama Lengkap</span>
                                                                <span className="font-medium text-slate-700 dark:text-slate-200">{reg.participant?.full_name}</span>
                                                                <span className="text-slate-400">Email</span>
                                                                <span>{reg.participant?.email}</span>
                                                                <span className="text-slate-400">No. WhatsApp</span>
                                                                <span>{reg.participant?.whatsapp_number || reg.participant?.phone || "-"}</span>
                                                                <span className="text-slate-400">Institusi</span>
                                                                <span className="capitalize">{reg.participant?.institution || "-"}</span>
                                                            </div>
                                                        </div>

                                                        <div className="space-y-3">
                                                            <div className="flex items-center gap-2 font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                                                                <PackageIcon className="w-4 h-4 text-emerald-500" /> Data Pendaftaran
                                                            </div>
                                                            <div className="grid grid-cols-[100px_1fr] gap-2">
                                                                <span className="text-slate-400">ID Registrasi</span>
                                                                <span>#{reg.registration_id}</span>
                                                                <span className="text-slate-400">No. Invoice</span>
                                                                <span className="font-mono">{reg.registration_number || "-"}</span>
                                                                <span className="text-slate-400">Tipe Daftar</span>
                                                                <span className="capitalize">{reg.registration_type || "Kolektif / Personal"}</span>
                                                                <span className="text-slate-400">Tanggal</span>
                                                                <span>{reg.registration_date ? new Date(reg.registration_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : "-"}</span>
                                                            </div>
                                                        </div>

                                                        <div className="space-y-3">
                                                            <div className="flex items-center gap-2 font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                                                                <Nfc className="w-4 h-4 text-indigo-500" /> Status RFID & Cetak
                                                            </div>
                                                            <div className="grid grid-cols-[100px_1fr] gap-2">
                                                                <span className="text-slate-400">UID Kartu</span>
                                                                <span className={reg.rfid_number ? "font-mono font-bold text-indigo-600 dark:text-indigo-400" : ""}>{reg.rfid_number || "Belum ada"}</span>
                                                                <span className="text-slate-400">Status Cetak</span>
                                                                <span>{reg.is_printed ? `Sudah Dicetak${reg.print_batch != null ? ` (Gel. ${reg.print_batch})` : ""}` : "Belum Dicetak"}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {pagination.total > 0 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-b-3xl">
                        <div className="flex items-center gap-4">
                            <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                Menampilkan <span className="text-slate-900 dark:text-white">{pagination.from || 0}</span> - <span className="text-slate-900 dark:text-white">{pagination.to || 0}</span> dari <span className="text-slate-900 dark:text-white">{pagination.total || 0}</span> data
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                    className="h-9 px-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold text-slate-700 dark:text-slate-300 text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="15">15</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1 || isFetching}
                                className="h-9 rounded-xl border-slate-200 dark:border-slate-700"
                            >
                                Sebelumnya
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => p + 1)}
                                disabled={currentPage >= (pagination.last_page || 1) || isFetching}
                                className="h-9 rounded-xl border-slate-200 dark:border-slate-700"
                            >
                                Selanjutnya
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Detail & Cetak Kartu */}
            <Dialog open={!!selectedParticipant} onOpenChange={(open) => !open && setSelectedParticipant(null)}>
                <DialogContent showCloseButton={false} className="!max-w-[90vw] sm:!max-w-[900px] w-full max-h-[92vh] overflow-hidden flex flex-col rounded-[2rem] p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl">
                    <DialogHeader className="shrink-0 p-5 sm:p-6 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-left">
                        <div className="flex items-start gap-3.5 min-w-0 pr-8">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-200/60 dark:border-indigo-800/60 shadow-sm">
                                <UserCircle2 className="w-7 h-7" />
                            </div>
                            <div className="min-w-0">
                                <DialogTitle className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-tight truncate">
                                    {selectedParticipant?.participant?.full_name || "-"}
                                </DialogTitle>
                                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                    <span>Tipe Daftar: <strong className="font-semibold text-slate-700 dark:text-slate-300 capitalize">{selectedParticipant?.registration_type || "Kolektif / Personal"}</strong></span>
                                </div>
                            </div>
                        </div>
                        <DialogClose className="absolute right-6 top-6 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 dark:ring-offset-slate-950 dark:focus:ring-slate-300 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-400">
                            <X className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </DialogClose>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto overflow-x-hidden p-5 sm:p-8 flex flex-col lg:flex-row gap-8 items-start w-full min-h-0">
                        <div className="w-full lg:w-[360px] shrink-0 space-y-4 mx-auto lg:mx-0 overflow-y-auto max-h-full pr-2 pb-4 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
                            <Tabs defaultValue="front" className="w-full">
                                <TabsList className="grid w-full grid-cols-2 mb-4 h-11 bg-slate-100 dark:bg-slate-800/80 rounded-xl p-1">
                                    <TabsTrigger value="front" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-sm font-bold">Depan</TabsTrigger>
                                    <TabsTrigger value="back" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-sm font-bold">Belakang</TabsTrigger>
                                </TabsList>

                                <TabsContent value="front" className="space-y-4 mt-0 border-0 p-0 focus-visible:ring-0">
                                    <div className="p-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-[2rem] shadow-xl">
                                        {isLoadingCard ? (
                                            <div className="w-full aspect-[54/86] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 animate-pulse">
                                                <Loader2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
                                                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Memuat Kartu...</span>
                                            </div>
                                        ) : cardImageUrl ? (
                                            <img
                                                src={cardImageUrl}
                                                alt="ID Card KONASPI (Depan)"
                                                className="w-full h-auto object-contain rounded-2xl border border-slate-200/50 dark:border-slate-700/50 pointer-events-none shadow-sm"
                                            />
                                        ) : (
                                            <div className="w-full aspect-[54/86] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 p-6 text-center">
                                                <ImageIcon className="w-10 h-10 opacity-40" />
                                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                                    Kartu (Depan) tidak dapat dimuat.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        onClick={handlePrintCardFromModal}
                                        disabled={!cardImageUrl || isLoadingCard}
                                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 rounded-2xl shadow-lg shadow-emerald-500/25 transition-all active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        <Printer className="w-4 h-4" />
                                        <span>Print Depan</span>
                                    </Button>
                                </TabsContent>

                                <TabsContent value="back" className="space-y-4 mt-0 border-0 p-0 focus-visible:ring-0">
                                    <div className="p-3 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-[2rem] shadow-xl">
                                        {isLoadingCard ? (
                                            <div className="w-full aspect-[54/86] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 animate-pulse">
                                                <Loader2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
                                                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Memuat Kartu...</span>
                                            </div>
                                        ) : cardBackImageUrl ? (
                                            <img
                                                src={cardBackImageUrl}
                                                alt="ID Card KONASPI (Belakang)"
                                                className="w-full h-auto object-contain rounded-2xl border border-slate-200/50 dark:border-slate-700/50 pointer-events-none shadow-sm"
                                            />
                                        ) : (
                                            <div className="w-full aspect-[54/86] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700 p-6 text-center">
                                                <ImageIcon className="w-10 h-10 opacity-40" />
                                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                                    Kartu (Belakang) tidak dapat dimuat.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        onClick={handlePrintCardBackFromModal}
                                        disabled={!cardBackImageUrl || isLoadingCard}
                                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 rounded-2xl shadow-lg shadow-emerald-500/25 transition-all active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        <Printer className="w-4 h-4" />
                                        <span>Print Belakang</span>
                                    </Button>
                                </TabsContent>
                            </Tabs>
                        </div>

                        <div className="flex-1 min-w-0 w-full space-y-6">
                            <div className="space-y-4">
                                <h3 className="font-bold text-lg">Informasi Partisipan</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800">
                                        <span className="text-slate-500 flex items-center gap-2"><CreditCard className="w-4 h-4" /> No Registrasi</span>
                                        <span className="font-bold text-right">{selectedParticipant?.registration_number || selectedParticipant?.registration_id}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Mail className="w-4 h-4" /> Email</span>
                                        <span className="font-bold text-right break-all">{selectedParticipant?.participant?.email}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Phone className="w-4 h-4" /> No. HP/WA</span>
                                        <span className="font-bold text-right">{selectedParticipant?.participant?.whatsapp_number || selectedParticipant?.participant?.phone || "-"}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Building className="w-4 h-4" /> Institusi</span>
                                        <span className="font-bold text-right line-clamp-2">{selectedParticipant?.participant?.institution || "-"}</span>
                                    </div>
                                    <div className="flex justify-between pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><PackageIcon className="w-4 h-4" /> Paket</span>
                                        <span className="font-bold text-right">{selectedParticipant?.package_name || selectedParticipant?.package?.name || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Status Bayar</span>
                                        <div className="flex items-center gap-2">
                                            <span className={`font-bold uppercase text-right text-xs px-2 py-0.5 rounded-md ${selectedParticipant?.payment_status === "paid"
                                                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                                    : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                                }`}>
                                                {selectedParticipant?.payment_status}
                                            </span>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="h-6 w-6 shrink-0"
                                                onClick={handleCheckParticipantPaymentStatus}
                                                disabled={isCheckingPaymentStatus}
                                                title="Sinkronkan status bayar dari gateway"
                                            >
                                                <RefreshCw className={`w-3 h-3 ${isCheckingPaymentStatus ? "animate-spin" : ""}`} />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Nfc className="w-4 h-4" /> Status RFID</span>
                                        <div className="flex items-center gap-2">
                                            <span className={`font-bold text-right text-xs px-2 py-0.5 rounded-md ${selectedParticipant?.rfid_number
                                                    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
                                                    : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
                                                }`}>
                                                {selectedParticipant?.rfid_number ? "Terdaftar" : "Belum Ada"}
                                            </span>
                                            {selectedParticipant?.rfid_number && (
                                                <Button
                                                    variant="destructive"
                                                    size="icon"
                                                    className="h-6 w-6 shrink-0"
                                                    onClick={() => handleDeleteRfid(selectedParticipant.registration_id, selectedParticipant.rfid_number as string)}
                                                    disabled={deleteRfidMutation.isPending}
                                                    title="Hapus RFID"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800 gap-4">
                                        <span className="text-slate-500 flex items-center gap-2"><Printer className="w-4 h-4" /> Status Cetak</span>
                                        <span className={`font-bold text-right text-xs px-2 py-0.5 rounded-md ${selectedParticipant?.is_printed
                                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                                : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                                            }`}>
                                            {selectedParticipant?.is_printed ? `Sudah Dicetak${selectedParticipant?.print_batch != null ? ` (Gel. ${selectedParticipant.print_batch})` : ""}` : "Belum Dicetak"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Tindakan Tambahan */}
                            <div className="space-y-4 pt-4">
                                <h3 className="font-bold text-lg">Tindakan Cepat</h3>
                                <div className="space-y-3 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                            <Nfc className="w-3.5 h-3.5" />
                                            {selectedParticipant?.rfid_number ? "Perbarui / Ganti RFID" : "Daftarkan RFID"}
                                        </label>
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                if (!rfidCodeForModal.trim() || !selectedParticipant) return;
                                                assignMutation.mutate({
                                                    registrationId: selectedParticipant.registration_id,
                                                    rfidCardId: rfidCodeForModal.trim()
                                                });
                                            }}
                                            className="flex gap-2"
                                        >
                                            <input
                                                type="text"
                                                autoFocus
                                                value={rfidCodeForModal}
                                                onChange={(e) => setRfidCodeForModal(e.target.value)}
                                                disabled={assignMutation.isPending}
                                                placeholder="Scan/Ketik nomor RFID..."
                                                className="flex-1 h-10 bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-600 rounded-xl text-sm px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-bold text-slate-800 dark:text-slate-100 placeholder:text-slate-400 placeholder:font-normal transition-all"
                                            />
                                            <Button
                                                type="submit"
                                                disabled={assignMutation.isPending || !rfidCodeForModal.trim()}
                                                className="h-10 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md shadow-indigo-500/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {assignMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
                                            </Button>
                                        </form>
                                        {selectedParticipant?.rfid_number && (
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                                                RFID Terdaftar: <span className="font-mono text-slate-700 dark:text-slate-300">{selectedParticipant.rfid_number}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-3 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                            <Printer className="w-3.5 h-3.5" />
                                            Ubah Status Cetak
                                        </label>
                                        <div className="flex gap-2 w-full">
                                            <Button
                                                onClick={() => {
                                                    if (!selectedParticipant) return;
                                                    togglePrintMutation.mutate({
                                                        registrationId: selectedParticipant.registration_id,
                                                        action: selectedParticipant.is_printed ? 'unmark' : 'mark'
                                                    });
                                                }}
                                                disabled={togglePrintMutation.isPending}
                                                className={`flex-1 h-9 rounded-xl shadow-md transition-all active:scale-95 flex items-center justify-center gap-2 ${selectedParticipant?.is_printed
                                                        ? "bg-rose-600 hover:bg-rose-700 text-white shadow-rose-500/20"
                                                        : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-500/20"
                                                    }`}
                                            >
                                                {togglePrintMutation.isPending ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : selectedParticipant?.is_printed ? (
                                                    <>
                                                        <XCircle className="w-4 h-4" />
                                                        Batal Cetak
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircle2 className="w-4 h-4" />
                                                        Tandai Cetak
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                        <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                                            Berguna jika ID Card dicetak secara fisik tanpa memindai secara otomatis.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Konfirmasi Set Tercetak (gelombang cetak massal) */}
            <AlertDialog open={isBulkMarkConfirmOpen} onOpenChange={setIsBulkMarkConfirmOpen}>
                <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            Tandai Sudah Dicetak
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                            {bulkMarkConfirmMessage}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 gap-2">
                        <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmBulkMarkPrinted}
                            className="rounded-xl h-11 font-bold text-white shadow-md bg-blue-600 hover:bg-blue-700"
                        >
                            Ya, Tandai
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Konfirmasi Hapus RFID */}
            <AlertDialog open={!!rfidToDelete} onOpenChange={(open) => !open && setRfidToDelete(null)}>
                <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-rose-500" />
                            Hapus/Lepas RFID
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                            Apakah Anda yakin ingin menghapus/melepas kartu RFID <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{rfidToDelete?.rfidNumber}</span> dari peserta ini?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 gap-2">
                        <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteRfid}
                            disabled={deleteRfidMutation.isPending}
                            className="rounded-xl h-11 font-bold text-white shadow-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50"
                        >
                            {deleteRfidMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Ya, Hapus
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
